import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import '../../../Common/ShimmerLoader.css';
import { DeleteModal } from '../../../Common/DeleteModal';
import { SellerSuccessModal } from '../Sellers/components/SellerSuccessModal';
import { getProducts, deleteProduct, updateProductStatus } from '../../../api/shop/products/productService';
import { Product } from '../../../api/shop/types/productTypes';
import ProductCard from './components/ProductCard';
import { SearchBar, FilterDropdown, ActionButton, FilterContainer } from '../../../components/ShopServices/Filters';
import { CatalogueConfigProvider, useCatalogueConfig } from '../../../contexts/CatalogueConfigContext';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import Pagination from '../Orders/components/Pagination';
import '../../../Common/BadgeStyles.css';
import './Products.css';

// Hooks
import { useDebounce } from '../../../hooks/useDebounce';

// Internal component that uses the context
const ProductsPageContent: React.FC = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.product.manage',
  ]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  // Search input state (for immediate UI updates)
  const [searchInputValue, setSearchInputValue] = useState('');
  // Debounce search input to prevent excessive API calls (300ms delay)
  const search = useDebounce(searchInputValue, 300);
  const [statusFilter, setStatusFilter] = useState<'All' | 'ACTIVE' | 'INACTIVE' | 'DRAFT'>('All');
  const [sportFilter, setSportFilter] = useState<string>('All');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // AbortController ref for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Use shared config from context instead of individual API call
  const { config, loading: configLoading } = useCatalogueConfig();

  // Dynamic filter options from config API
  const sportOptions = [
    { value: 'All', label: 'All Sports' },
    ...(config?.sports.map(sport => ({
      value: sport.value,
      label: sport.label
    })) || [])
  ];

  const categoryOptions = [
    { value: 'All', label: 'All Categories' },
    ...(config ? [
      ...config.categories.apparels.map(cat => ({ value: cat.value, label: cat.label })),
      ...config.categories.accessories.map(cat => ({ value: cat.value, label: cat.label }))
    ] : [])
  ];

  const statusOptions = [
    { value: 'All', label: 'All Status' },
    ...(config?.status.map(status => ({
      value: status.value,
      label: status.label
    })) || [])
  ];

  const fetchProducts = useCallback(async () => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      setLoading(true);
      const filters: any = {
        pageNum: page,
        pageSize: pageSize,
      };
      
      if (statusFilter !== 'All') {
        filters.status = statusFilter;
      }
      if (sportFilter !== 'All') {
        filters.sport = sportFilter;
      }
      if (categoryFilter !== 'All') {
        filters.category = categoryFilter;
      }
      if (search) {
        filters.search = search;
      }

      const response = await getProducts(filters);
      
      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }
      
      setProducts(response.paginatedResults || []);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
    } catch (error: any) {
      // Don't set error if request was aborted (user changed filters or component unmounted)
      if (abortController.signal.aborted || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      // Error handled silently for other errors
    } finally {
      // Only update loading state if request wasn't aborted
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [page, pageSize, statusFilter, sportFilter, categoryFilter, search]);

  useEffect(() => {
    fetchProducts();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchProducts]);

  const handleAddProduct = () => {
    if (!hasManagePermissions) return;
    navigate('/shop-services/products/add');
  };

  const handleEditProduct = (product: Product) => {
    // Navigation handled directly in ProductCard component
  };

  const handleDeleteClick = (product: Product) => {
    if (!hasManagePermissions) return;
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!productToDelete) return;
    
    try {
      setDeleting(true);
      await deleteProduct(productToDelete._id);
      
      setShowDeleteModal(false);
      fetchProducts();
    } catch (error) {
      // Error handled silently
    } finally {
      setDeleting(false);
      setProductToDelete(null);
    }
  };

  const handleToggleStatus = async (product: Product) => {
    if (!hasManagePermissions) return;
    const newStatus = product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
    // Optimistically update the local state immediately
    setProducts(prevProducts => 
      prevProducts.map(p => 
        p._id === product._id ? { ...p, status: newStatus } : p
      )
    );
    
    try {
      await updateProductStatus(product._id, newStatus);
    } catch (error) {
      // Revert on error - refetch to sync with server state
      fetchProducts();
    }
  };

  // Form success handling moved to ProductDetail page

  const filteredProducts = products.filter(product => {
    if (search) {
      const searchLower = search.toLowerCase();
      return (
        product.name?.toLowerCase().includes(searchLower) ||
        product.productId?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  return (
    <div className="page-content" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/products') },
            { label: 'Products', active: true }
          ]} />
        </div>

        {/* Header Section */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="page-title mb-0">
              {totalCount} {totalCount === 1 ? 'Product' : 'Products'}
            </h1>
            <FilterContainer>
              <SearchBar
                value={searchInputValue}
                onChange={setSearchInputValue}
                placeholder="Search by name or ID"
                width="200px"
              />
              <FilterDropdown
                value={sportOptions.find(opt => opt.value === sportFilter) || null}
                onChange={(option) => {
                  if (option) {
                    setSportFilter(option.value);
                    setPage(1);
                    setPageSize(10); // Reset to default page size when filter changes
                  }
                }}
                options={sportOptions}
                placeholder="Select sport..."
                width="150px"
                isLoading={configLoading}
              />
              <FilterDropdown
                value={categoryOptions.find(opt => opt.value === categoryFilter) || null}
                onChange={(option) => {
                  if (option) {
                    setCategoryFilter(option.value);
                    setPage(1);
                    setPageSize(10); // Reset to default page size when filter changes
                  }
                }}
                options={categoryOptions}
                placeholder="Select category..."
                width="150px"
                isLoading={configLoading}
              />
              <FilterDropdown
                value={statusOptions.find(opt => opt.value === statusFilter) || null}
                onChange={(option) => {
                  if (option) {
                    setStatusFilter(option.value as 'All' | 'ACTIVE' | 'INACTIVE' | 'DRAFT');
                    setPage(1);
                    setPageSize(10); // Reset to default page size when filter changes
                  }
                }}
                options={statusOptions}
                placeholder="Select status..."
                width="150px"
                isLoading={configLoading}
              />
              {hasManagePermissions && (
                <ActionButton onClick={handleAddProduct}>
                  Add Product +
                </ActionButton>
              )}
            </FilterContainer>
          </div>
        </div>

        {/* Content Section */}
        <div style={{ padding: '0 15px' }}>
          {loading ? (
            <div className="products-grid">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="card" style={{ width: '100%', maxWidth: '376px', minWidth: '320px', height: '151px', padding: '12px', borderRadius: '16px', boxSizing: 'border-box' }}>
                  <div className="placeholder" style={{ width: '100%', height: '127px', borderRadius: '12px' }}></div>
                </div>
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No products found</p>
            </div>
          ) : (
            <div className="products-grid">
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onEdit={hasManagePermissions ? () => handleEditProduct(product) : undefined}
                  onDelete={hasManagePermissions ? () => handleDeleteClick(product) : undefined}
                  onToggleStatus={
                    hasManagePermissions ? () => handleToggleStatus(product) : undefined
                  }
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={(newPage) => {
                setPage(newPage);
              }}
              onPageSizeChange={(newPageSize) => {
                setPageSize(newPageSize);
                setPage(1); // Reset to first page when page size changes
              }}
            />
          )}
        </div>
      </Container>

      {/* Form functionality moved to dedicated ProductDetail page */}

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setProductToDelete(null);
        }}
        deleteModalFunction={handleDeleteConfirm}
        title="Delete Product"
        message={`Are you sure you want to delete "${productToDelete?.name}"? This action cannot be undone.`}
        isLoading={deleting}
      />

    </div>
  );
};

// Main component with context provider
const ProductsPage: React.FC = () => {
  return (
    <CatalogueConfigProvider>
      <ProductsPageContent />
    </CatalogueConfigProvider>
  );
};

export default ProductsPage;
